<template>
  <div class="container mt-4">
    <h1>📝 AdvancedSelect</h1>
    <p>
      A powerful Vue 3 select component supporting:
      <strong>single/multiple selection</strong>,
      <strong>tag display</strong>,
      <strong>searching/filtering</strong>,
      <strong>tooltips</strong>,
      <strong>custom rendering</strong>, and <strong>keyboard navigation</strong>.
      Works with <code>v-model</code> and emits useful events for selection, search, and dropdown state.
    </p>
    <hr />

    <h2>⚙️ Props</h2>
    <ul>
      <li><code>id</code> <strong>String</strong> — optional HTML id for the input.</li>
      <li><code>items</code> <strong>Array</strong> — array of options (objects with <code>value</code> and <code>text</code> by default).</li>
      <li><code>searchable</code> <strong>Boolean</strong> — enable search/filtering.</li>
      <li><code>disabled</code> <strong>Boolean</strong> — disables the input.</li>
      <li><code>limitedWrapperSize</code> <strong>Boolean</strong> — limits dropdown width to parent input width.</li>
      <li><code>showMainTooltip</code>, <code>showOptionTooltip</code> <strong>Boolean</strong> — enable tooltips on main input or options.</li>
      <li><code>upperCase</code> <strong>Boolean</strong> — force uppercase display.</li>
      <li><code>dropdownAlign</code> <strong>String</strong> — dropdown alignment: <code>'left'</code> (default) or <code>'right'</code>.</li>
      <li><code>dropdownWrapperClass</code>, <code>controlClass</code> <strong>String | Array | Object</strong> — custom classes for dropdown wrapper or input.</li>
      <li><code>modelValue</code> <strong>String | Number | Array</strong> — bound value via <code>v-model</code>.</li>
      <li><code>customSearchHandler</code> <strong>Function</strong> — custom function to handle search.</li>
      <li><code>valueField</code>, <code>textField</code>, <code>selectedTextField</code> <strong>String</strong> — keys for value and display text.</li>
      <li><code>emptyListText</code> <strong>String</strong> — text shown when no items found.</li>
      <li><code>multiple</code> <strong>Boolean</strong> — enable multi-selection.</li>
      <li><code>searchPlaceholder</code> <strong>String</strong> — placeholder for search input.</li>
      <li><code>closeOnSelect</code> <strong>Boolean</strong> — close dropdown after selecting an option.</li>
      <li><code>taggable</code> <strong>Boolean</strong> — show selected items as tags.</li>
      <li><code>multipleSelectedOptionsText</code> <strong>Function</strong> — template for multiple selected items display.</li>
      <li><code>autoAdjustValuesToItems</code> <strong>Boolean</strong> — automatically syncs model values with items.</li>
      <li><code>hasError</code> <strong>Boolean</strong> — shows input in error state.</li>
      <li><code>hideClearIcon</code>, <code>isLoadingOption</code>, <code>showTagTooltip</code> <strong>Boolean</strong> — control UI states.</li>
      <li><code>size</code> <strong>String</strong> — input size: <code>'sm'</code>, <code>'md'</code>, <code>'lg'</code>.</li>
      <li><code>sortByAbc</code>, <code>sortExactSearchResultsAtTop</code>, <code>autoSelectOnlyOptionOnSearch</code> <strong>Boolean</strong> — sorting and selection behavior.</li>
      <li><code>limit</code> <strong>Number</strong> — maximum number of selectable items.</li>
      <li><code>multipleAddNewOption</code> <strong>Boolean</strong> — allow adding new options in multi-select mode.</li>
      <li><code>getItemClass</code> <strong>Function</strong> — returns additional classes for each item.</li>
      <li><code>inputClass</code> <strong>String</strong> — custom class for input field.</li>
      <li><code>tagBgColor</code> <strong>String | Function</strong> — tag background color.</li>
      <li><code>canRemoveItem</code> <strong>Function</strong> — callback to control if a tag can be removed.</li>
      <li><code>splitter</code> <strong>String</strong> — character to split multiple input values.</li>
      <li><code>tagDisplayLimit</code> <strong>Number</strong> — maximum tags displayed before showing "+X more".</li>
      <li><code>comparisonMode</code> <strong>String</strong> — custom comparison mode (e.g., '=', '!=').</li>
    </ul>

    <h2>🎯 Emits</h2>
    <ul>
      <li><code>update:modelValue</code> — emits the current selected value(s).</li>
      <li><code>select</code> / <code>deselect</code> — fires on option selection/deselection.</li>
      <li><code>input</code> / <code>change</code> — fires when input or selection changes.</li>
      <li><code>dropdownShow</code> / <code>dropdownHide</code> — fires when dropdown opens/closes.</li>
      <li><code>reset</code> — fired when selection is cleared.</li>
      <li><code>searchTextChange</code> — emits the current search string.</li>
      <li><code>selectedItemsChange</code> — emits array of selected items.</li>
    </ul>

    <h2>💻 Usage Examples</h2>

    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h3>Basic Multi-Select with Tags</h3>
    <AdvancedSelect
        v-model="multiSelected"
        :items="options"
        searchable
        multiple
        taggable
    />
    <p>Selected Values: <strong>{{ multiSelected }}</strong></p>
    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="multiSelected"
        :items="options"
        searchable
        multiple
        taggable
      /&gt;
    </code></pre>

    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h3>Single Select with Search</h3>
    <AdvancedSelect
        v-model="singleSelected"
        :items="options"
        :searchable="true"
        placeholder="Select one option..."
    />
    <p>Selected Value: <strong>{{ singleSelected }}</strong></p>
    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="singleSelected"
        :items="options"
        :searchable="true"
        placeholder="Select one option..."
      /&gt;
    </code></pre>
    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h3>Limited Selection</h3>
    <AdvancedSelect
        v-model="limitedSelected"
        :items="options"
        :multiple="true"
        :taggable="true"
        :limit="2"
        placeholder="Select up to 2 options..."
    />
    <p>Selected Values: <strong>{{ limitedSelected }}</strong></p>
    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="limitedSelected"
        :items="options"
        :multiple="true"
        :taggable="true"
        :limit="2"
        placeholder="Select up to 2 options..."
      /&gt;
    </code></pre>
    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h3>Error State</h3>
    <p>
      Use the <code>hasError</code> prop to highlight the input when there’s a validation error.
      The input will display a red border and remain interactive.
    </p>

    <AdvancedSelect
        v-model="errorSelected"
        :items="options"
        :hasError="!errorSelected"
        placeholder="Select an option"
    />

    <p>Selected value: <strong>{{ errorSelected }}</strong></p>
    <p v-if="!errorSelected" style="color: red;">⚠ You must select an option!</p>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="errorSelected"
        :items="options"
        :hasError="!errorSelected"
        placeholder="Select an option"
      /&gt;
    </code></pre>
    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h3>Add New Options (multipleAddNewOption)</h3>
    <AdvancedSelect
        v-model="selectedValues"
        :items="items"
        multiple
        searchable
        multipleAddNewOption
        :limit="5"
        placeholder="Type and press Enter..."
        @select="onSelect"
        @deselect="onDeselect"
    />

    <div style="margin-top: 10px;">
      <strong>Selected values:</strong>
      {{ selectedValues }}
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
        &lt;AdvancedSelect
          v-model="selectedValues"
          :items="items"
          multiple
          searchable
          multipleAddNewOption
          :limit="5"
          placeholder="Type and press Enter..."
          @select="onSelect"
          @deselect="onDeselect"
        /&gt;

        onSelect(item) {
          // Add newly created options into the list
          if (item.value.startsWith('new_')) {
            this.items.push(item);
          }
        }
    </code></pre>
    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h3>Tags with “+X more” Summary</h3>
    <p>
      This example shows how selected items are displayed as tags. chosen_multiple 5 options
    </p>

    <AdvancedSelect
        v-model="multiSelectedWithTags"
        :items="options"
        multiple
        :taggable="false"
        searchable
        placeholder="Select options..."
    />

    <p><strong>Selected values:</strong> {{ multiSelectedWithTags }} </p>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="multiSelectedWithTags"
        :items="options"
        multiple
        :taggable="false"
        :searchable="false"
        placeholder="Select options..."
      /&gt;
    </code></pre>
    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h3>Tags + “+X more” using tagDisplayLimit</h3>
    <p>
      This example combines visible tags with a custom summary using <code>tagDisplayLimit</code>.
      When the number of selected items exceeds the tag display limit, the remaining items are shown as <code>+X more</code>.
    </p>

    <AdvancedSelect
        v-model="multiSelectedSmart"
        :items="options"
        multiple
        taggable
        :tagDisplayLimit="3"
        :searchable="false"
        placeholder="Select options..."
    />

    <p> <strong>Selected values:</strong> {{ multiSelectedSmart }} </p>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="multiSelectedSmart"
        :items="options"
        multiple
        taggable
        :tagDisplayLimit="3"
        :searchable="false"
        placeholder="Select options..."
      /&gt;
    </code></pre>
    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h3>Tooltips Example with showTagTooltip and showOptionTooltip</h3>
    <AdvancedSelect
        v-model="selectedItem"
        :items="options"
        placeholder="Select an item"
        multiple
        showTagTooltip
        :showOptionTooltip="true"
    />

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="selectedItem"
        :items="options"
        placeholder="Select an item"
        multiple
        showTagTooltip
        :showOptionTooltip="true"
      /&gt;

      options: [
        { value: 1, text: 'Option 1' },
        { value: 2, text: 'Option 2', description: 'bbb' },
        { value: 3, text: 'Option 3', description: 'ccc' },
        { value: 4, text: 'Option 4', description: 'ddd' },
        { value: 5, text: 'Option 5', description: 'eee' }
      ],
    </code></pre>

    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h1>🕑 AdvancedSelect with Loading State</h1>
    <p>
      The <strong>isLoadingOption</strong> prop shows a spinner inside the dropdown while data is being loaded.
      This is useful for asynchronous data fetching.
    </p>

    <AdvancedSelect
        v-model="selectedValue"
        :items="list"
        placeholder="Select an option"
        :isLoadingOption="isLoading"
        searchable
        size="md"
    />

    <div class="mt-3">
      <button class="btn btn-sm btn-primary me-2" @click="fetchItems">
        Fetch Items
      </button>
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="selectedValue"
        :items="items"
        placeholder="Select an option"
        :isLoadingOption="isLoading"
        searchable
        size="md"
      /&gt;
    </code></pre>

    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h1>🔍 Custom Search Handler</h1>
    <p>
      Filters items that <strong>start with</strong> the typed text, instead of the default <code>includes</code>.
      Supports asynchronous search via an API.
    </p>

    <AdvancedSelect
        v-model="selectedUser"
        :items="userOptions"
        :searchable="true"
        :customSearchHandler="customSearchHandler"
        :isLoadingOption="isLoading"
        :showOptionTooltip="true"
        textField="name"
        valueField="id"
        placeholder="Search user..."
        @searchTextChange="handleSearch"
    />

    <p class="mt-3">
      Selected value: <strong>{{ selectedUser }}</strong>
    </p>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="selectedUser"
        :items="userOptions"
        :searchable="true"
        :customSearchHandler="customSearchHandler"
        :isLoadingOption="isLoading"
        :showOptionTooltip="true"
        textField="name"
        valueField="id"
        placeholder="Search user..."
        @searchTextChange="handleSearch"
      /&gt;

      methods: {
        async handleSearch(searchText) {
          this.isLoading = true
          try {
            const { data } = await axios.get(
                `https://jsonplaceholder.typicode.com/users`,
                { params: { q: searchText } }
            )
            this.userOptions = data.map(user => ({
              id: user.id,
              name: user.name,
              description: user.email
            }))
          } catch (e) {
            console.error('API error:', e)
            this.userOptions = []
          } finally {
            this.isLoading = false
          }
        },

        customSearchHandler(searchText, items) {
          return items.filter(item =>
              item.name.toLowerCase().startsWith(searchText.toLowerCase())
          )
        }
      }
    </code></pre>

    <p>Notes:</p>
    <ul>
      <li>The input is active because <code>searchable</code> is <code>true</code>.</li>
      <li>Async search is triggered via <code>@searchTextChange="handleSearch"</code>.</li>
      <li>The loaded items are filtered using the <code>customSearchHandler</code>.</li>
      <li>Loading indicator is displayed using <code>isLoadingOption</code>.</li>
    </ul>

    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h1>⚖️ Comparison Mode (AdvancedSelect)</h1>

    <p>
      The <code>comparisonMode</code> feature allows the select component to behave like a
      <strong>filter operator switch</strong>. Instead of only selecting values, the user can
      toggle between two comparison states:
      <code>=</code> (equal) and <code>!=</code> (not equal).
    </p>

    <hr />

    <h2>🧠 How it works</h2>

    <p>
      When <code>comparisonMode</code> is enabled, a small toggle button appears inside the input group.
      Clicking it switches between:
    </p>

    <ul>
      <li><code>=</code> → match selected values (IN filter)</li>
      <li><code>!=</code> → exclude selected values (NOT IN filter)</li>
    </ul>

    <p>This makes the component useful for dashboards, admin filters, and query builders.</p>

    <hr />

    <h2>🧪 Live Example</h2>

    <p>Below is a real usage example where <code>AdvancedSelect</code> controls filtering of a user list.</p>

    <AdvancedSelect
        v-model="selectedRoles"
        :items="roles"
        :multiple="true"
        comparisonMode="="
        @comparisonModeChange="mode = $event"
    />
    <div style="margin-top: 20px">
      <p><strong>Mode:</strong> {{ mode }}</p>
      <p><strong>Selected roles:</strong> {{ selectedRoles }}</p>
      <h3>Users</h3>
      <ul>
        <li v-for="u in filteredUsers" :key="u.id"> {{ u.name }} — {{ u.role }} </li>
      </ul>
    </div>

    <h3>📦 Template</h3>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="selectedRoles"
        :items="roles"
        :multiple="true"
        comparisonMode="="
        @comparisonModeChange="mode = $event"
      /&gt;

      &lt;div style="margin-top: 20px"&gt;
        &lt;p&gt;&lt;strong&gt;Mode:&lt;/strong&gt; <span v-pre>{{ mode }}</span>&lt;/p&gt;
        &lt;p&gt;&lt;strong&gt;Selected roles:&lt;/strong&gt; <span v-pre>{{ selectedRoles }}</span>&lt;/p&gt;

        &lt;h3&gt;Users&lt;/h3&gt;

        &lt;ul&gt;
          &lt;li v-for="u in filteredUsers" :key="u.id"&gt;
            <span v-pre>{{ u.name }} — {{ u.role }}</span>
          &lt;/li&gt;
        &lt;/ul&gt;
      &lt;/div&gt;
    </code></pre>

    <hr />

    <h3>🧠 Script (Filtering Logic)</h3>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      computed: {
        filteredUsers() {
          if (!this.selectedRoles.length) {
            return this.users;
          }

          // "=" mode → include selected roles
          if (this.mode === '=') {
            return this.users.filter(u =>
              this.selectedRoles.includes(u.role)
            );
          }

          // "!=" mode → exclude selected roles
          if (this.mode === '!=') {
            return this.users.filter(u =>
              !this.selectedRoles.includes(u.role)
            );
          }

          return this.users;
        }
      }
    </code></pre>

    <hr />

    <h2>⚙️ Real-world Behavior</h2>

    <ul>
      <li>User selects one or more roles from the AdvancedSelect</li>
      <li>User toggles comparison mode (<code>=</code> or <code>!=</code>)</li>
      <li>Computed property recalculates filtered results instantly</li>
    </ul>

    <p>
      This allows the select to behave like a <strong>mini query builder</strong>.
    </p>

    <hr />

    <h2>💡 UX Summary</h2>

    <ul>
      <li><code>=</code> → show only selected values</li>
      <li><code>!=</code> → exclude selected values</li>
      <li>Mode is independent from selection state</li>
      <li>Works in real-time with computed filtering</li>
    </ul>

    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h1>✂️ Splitter Feature (AdvancedSelect)</h1>

    <p>
      The <code>splitter</code> feature allows a single selected option to be automatically
      split into multiple values based on a delimiter character (for example: <code>,</code> or <code>;</code>).
    </p>

    <hr />

    <h2>🧠 How it works</h2>

    <p>
      If an item’s text contains the <code>splitter</code> character, the component will:
    </p>

    <ul>
      <li>Split the text into multiple parts</li>
      <li>Create separate selectable values for each part</li>
      <li>Respect the <code>limit</code> of selected items</li>
    </ul>

    <p>
      This is useful when one option actually represents multiple values (e.g. tags, categories, keywords).
    </p>

    <hr />

    <h2>🧪 Live Example</h2>

    <p>
      In this example, selecting a value like <code>“Vue, React, Angular”</code> will automatically
      create three separate selected items.
    </p>

    <h2>✂️ Splitter Live Demo</h2>

    <!-- LIVE COMPONENT -->
    <AdvancedSelect
        v-model="selectedFrameworks"
        :items="frameworkOptions"
        :multiple="true"
        splitter=","
        @change="onChange"
    />

    <hr />

    <h3>🧩 Selected Items (after split)</h3>

    <ul>
      <li v-for="(item, index) in selectedFrameworks" :key="index">
        {{ item }}
      </li>
    </ul>

    <hr />

    <p><strong>Raw model value:</strong></p>
    <pre>{{ selectedFrameworks }}</pre>

    <h3>📦 Template</h3>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="selectedFrameworks"
        :items="frameworkOptions"
        :multiple="true"
        splitter=","
      /&gt;
    </code></pre>

    <hr />

    <h3>🧠 Data Example</h3>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      data() {
        return {
          selectedFrameworks: [],

          frameworkOptions: [
            {
              text: "Vue, React, Angular",
              value: "frontend"
            },
            {
              text: "Node.js, Express",
              value: "backend"
            },
            {
              text: "PostgreSQL, MongoDB",
              value: "database"
            }
          ]
        };
      }
    </code></pre>

    <hr />

    <h3>⚙️ What happens when user selects an item</h3>

    <ul>
      <li>User selects <code>"Vue, React, Angular"</code></li>
      <li>Component detects <code>splitter=","</code></li>
      <li>Text is split into:
        <ul>
          <li>Vue</li>
          <li>React</li>
          <li>Angular</li>
        </ul>
      </li>
      <li>Each value becomes an individual selected tag</li>
    </ul>

    <hr />

    <h3>🧩 Result in UI</h3>

    <p>
      Instead of one selection, the user will see:
    </p>

    <ul>
      <li>Vue</li>
      <li>React</li>
      <li>Angular</li>
    </ul>

    <p>
      Each of them behaves like a normal selected item (can be removed individually).
    </p>

    <hr />

    <h2>💡 UX Use Cases</h2>

    <ul>
      <li>Bulk tag selection from a single option</li>
      <li>Importing comma-separated values</li>
      <li>Predefined grouped options (e.g. tech stacks)</li>
    </ul>

    <hr />

    <h2>⚠️ Important Notes</h2>

    <ul>
      <li><code>splitter</code> only works when <code>multiple = true</code></li>
      <li>Empty values are ignored automatically</li>
      <li>Selection limit (<code>limit</code>) is respected</li>
    </ul>

    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h1>🚪 closeOnSelect (AdvancedSelect)</h1>

    <p>
      The <code>closeOnSelect</code> prop controls whether the dropdown should automatically
      close after selecting an item.
    </p>

    <hr />

    <h2>🧠 How it works</h2>

    <ul>
      <li><code>true</code> → dropdown closes immediately after selection</li>
      <li><code>false</code> → dropdown stays open for multi-selection workflows</li>
    </ul>

    <p>
      This is especially useful for improving UX in multi-select scenarios where users need to pick multiple items quickly.
    </p>

    <hr />

    <h2>🧪 Live Example</h2>

    <label style="display:block; margin-bottom: 10px;">
      <input type="checkbox" v-model="closeOnSelect" />
      closeOnSelect = {{ closeOnSelect }}
    </label>

    <AdvancedSelect
        v-model="selectedRoles"
        :items="roles"
        :multiple="true"
        :closeOnSelect="closeOnSelect"
    />
    <p>Toggle the behavior by switching <code>closeOnSelect</code>.</p>
    <hr />

    <h2>⚙️ Example: Vue Component</h2>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="selectedRoles"
        :items="roles"
        :multiple="true"
        :closeOnSelect="closeOnSelect"
      /&gt;
    </code></pre>

    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h1>🎨 tagBgColor (AdvancedSelect / Tag)</h1>

    <p>
      The <code>tagBgColor</code> prop controls the background color of selected tags inside
      <code>AdvancedSelect</code>. It supports both a <strong>static class</strong> or a
      <strong>dynamic function</strong>.
    </p>

    <hr />

    <h2>🧠 How it works</h2>

    <ul>
      <li><code>String</code> → applies a fixed CSS class to all tags</li>
      <li><code>Function</code> → returns a dynamic class based on the selected item</li>
    </ul>

    <p>
      This allows you to visually distinguish different types of selected values.
    </p>

    <hr />

    <h2>🧪 Live Example</h2>

    <p>
      In this example, each role gets a different color:
    </p>

    <ul>
      <li>Admin → red tag</li>
      <li>Manager → blue tag</li>
      <li>User → green tag</li>
    </ul>

    <AdvancedSelect
        v-model="selectedRoles"
        :items="roles"
        :multiple="true"
        :tagBgColor="getTagColor"
    />

    <p><strong>Selected:</strong> {{ selectedRoles }}</p>

    <hr />

    <h3>🧠 Vue Example</h3>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="selectedRoles"
        :items="roles"
        :multiple="true"
        :tagBgColor="getTagColor"
      /&gt;

      export default {
        data() {
          return {
            selectedRoles: [],

            roles: [
              { text: "Admin", value: "admin" },
              { text: "Manager", value: "manager" },
              { text: "User", value: "user" }
            ]
          };
        },

        methods: {
          getTagColor(item) {
            switch (item.value) {
              case "admin":
                return "bg-danger text-white";

              case "manager":
                return "bg-primary text-white";

              case "user":
                return "bg-success text-white";

              default:
                return "bg-secondary text-white";
            }
          }
        }
      };
    </code></pre>

    <hr />

    <h2>🎯 Result in UI</h2>

    <ul>
      <li>Admin → 🔴 red tag</li>
      <li>Manager → 🔵 blue tag</li>
      <li>User → 🟢 green tag</li>
    </ul>

    <hr />

    <h2>💡 Use Cases</h2>

    <ul>
      <li>User role highlighting</li>
      <li>Status-based tags (active, pending, blocked)</li>
      <li>Category coloring in filters</li>
    </ul>

    <hr />

    <h2>⚠️ Notes</h2>

    <ul>
      <li><code>tagBgColor</code> is applied per selected item</li>
      <li>Function mode gives full control over styling logic</li>
      <li>Should return valid CSS class string</li>
    </ul>

    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h1>🗑️ canRemoveItem (AdvancedSelect / Tag)</h1>

    <p>
      The <code>canRemoveItem</code> prop controls whether a selected item can be removed from the selection.
      It is a <strong>function-based guard</strong> that enables fine-grained control over tag deletion.
    </p>

    <hr />

    <h2>🧠 How it works</h2>

    <p>
      <code>canRemoveItem</code> receives the selected item as an argument and must return a boolean:
    </p>

    <ul>
      <li><code>true</code> → item can be removed</li>
      <li><code>false</code> → remove button is disabled / ignored</li>
    </ul>

    <p>
      This is useful when some values are mandatory or system-critical.
    </p>

    <hr />

    <h2>🧪 Live Example</h2>

    <p>
      In this example, the <code>Admin</code> role cannot be removed.
    </p>

    <hr />

    <h3>📦 Template</h3>
    <AdvancedSelect
        v-model="selectedRoles"
        :items="roles"
        :multiple="true"
        :canRemoveItem="canRemoveItem"
    />

    <p><strong>Selected:</strong> {{ selectedRoles }}</p>

    <hr />

    <h3>🧠 Vue Example</h3>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="selectedRoles"
        :items="roles"
        :multiple="true"
        :canRemoveItem="canRemoveItem"
      /&gt;
    </code></pre>

    <hr />

    <h3>🧠 Script</h3>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      methods: {
        canRemoveItem(item) {
          if (item.value === "admin") {
            return false;
          }

          return true;
        }
      }
    </code></pre>

    <hr />

    <h2>🎯 Result in UI</h2>

    <ul>
      <li>Admin → ❌ cannot be removed</li>
      <li>Manager → ✅ removable</li>
      <li>User → ✅ removable</li>
    </ul>

    <hr />

    <h2>💡 Use Cases</h2>

    <ul>
      <li>Protect system roles (admin, owner)</li>
      <li>Prevent removal of required filters</li>
      <li>Enforce business rules in selection logic</li>
    </ul>

    <hr />

    <h2>⚠️ Notes</h2>

    <ul>
      <li><code>canRemoveItem</code> must return a boolean</li>
      <li>If it returns <code>false</code>, the tag remove button is disabled</li>
      <li>Works only in <code>multiple</code> mode</li>
    </ul>



    <hr style="border: none; height: 5px; background: #2c3e50; box-shadow: 0 0 8px rgba(44, 62, 80, 0.6); margin: 24px 0;" />
    <h1>🎯 getItemClass (AdvancedSelect)</h1>

    <p>
      The <code>getItemClass</code> prop allows you to dynamically assign CSS classes
      to each dropdown option based on its data.
    </p>

    <hr />

    <h2>🧠 How it works</h2>

    <p>
      <code>getItemClass</code> is a function that receives an item and returns a string
      (or object/array of classes). This class is applied to each option inside the dropdown.
    </p>

    <ul>
      <li>Used for conditional styling of options</li>
      <li>Helps visually distinguish item types or states</li>
      <li>Works only for dropdown options (not tags)</li>
    </ul>

    <hr />

    <h2>🧪 Live Example</h2>
    <AdvancedSelect
        v-model="selectedRoles"
        :items="roles"
        :multiple="true"
        :getItemClass="getItemClass"
    />

    <hr />

    <h3>🧠 Vue Example</h3>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;AdvancedSelect
        v-model="selectedRoles"
        :items="roles"
        :multiple="true"
        :getItemClass="getItemClass"
      /&gt;
    </code></pre>

    <hr />

    <h3>🧠 Script</h3>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      getItemClass(item) {
        switch (item.value) {
          case "admin":
            return "bg-danger text-white fw-bold";

          case "manager":
            return "bg-primary text-white";

          case "user":
            return "bg-success text-white";

          default:
            return "";
        }
      }
    </code></pre>

    <hr />

    <h2>🎯 Result in UI</h2>

    <ul>
      <li>Admin → 🔴 red highlighted option</li>
      <li>Manager → 🔵 blue highlighted option</li>
      <li>User → 🟢 green option</li>
      <li>Guest → default styling</li>
    </ul>

    <hr />

    <h2>💡 Use Cases</h2>

    <ul>
      <li>Role-based highlighting in dropdowns</li>
      <li>Status indicators (active, pending, disabled)</li>
      <li>Priority marking (high / medium / low)</li>
    </ul>

    <hr />

    <h2>⚠️ Notes</h2>

    <ul>
      <li><code>getItemClass</code> applies only to dropdown options</li>
      <li>Must return valid CSS class string or object</li>
      <li>Does not affect selected tags styling</li>
    </ul>
  </div>
</template>

<script>
import AdvancedSelect from '@/components/common/advanced-select/AdvancedSelect.vue';
import axios from 'axios'

export default {
  components: { AdvancedSelect },
  data() {
    return {
      closeOnSelect: false,
      mode: '=',
      selectedRoles: [],

      roles: [
        { text: 'Admin', value: 'admin' },
        { text: 'Manager', value: 'manager' },
        { text: 'User', value: 'user' }
      ],

      users: [
        { id: 1, name: 'John', role: 'admin' },
        { id: 2, name: 'Anna', role: 'manager' },
        { id: 3, name: 'Mike', role: 'user' },
        { id: 4, name: 'Sara', role: 'admin' }
      ],

      selectedValues: [],
      selectedValue: null,
      isLoading: false,
      limitedDropdown: null,
      selectedItems: [],
      selectedFrameworks: [],
      selectedItem: null,
      list: [],
      country: [],
      frameworkOptions: [
        {
          text: "Vue, React, Angular",
          value: "frontend"
        },
        {
          text: "Node.js, Express",
          value: "backend"
        },
        {
          text: "PostgreSQL, MongoDB",
          value: "database"
        }
      ],
      items: [
        { value: 1, text: 'Apple' },
        { value: 2, text: 'Banana' },
        { value: 3, text: 'Apricot' },
        { value: 4, text: 'Blueberry' },
      ],
      options: [
        { value: 1, text: 'Option 1' },
        { value: 2, text: 'Option 2', description: 'Description 2' },
        { value: 3, text: 'Option 3', description: 'Description 3' },
        { value: 4, text: 'Option 4', description: 'Description 4' },
        { value: 5, text: 'Option 5', description: 'Description 5' },
        { value: 6, text: 'Option 6', description: 'Description 6' },
        { value: 7, text: 'Option 7', description: 'Description 7' },
        { value: 8, text: 'Option 8', description: 'Description 8' },
        { value: 9, text: 'Option 9', description: 'Description 9' },
        { value: 10, text: 'Option 10', description: 'Description 10' },
        { value: 11, text: 'Option 11', description: 'Description 11' },
        { value: 12, text: 'Option 12', description: 'Description 12' },
        { value: 13, text: 'Option 13', description: 'Description 13' },
        { value: 14, text: 'Option 14', description: 'Description 14' },
        { value: 15, text: 'Option 15', description: 'Description 15' },
        { value: 16, text: 'Option 16', description: 'Description 16' },
        { value: 17, text: 'Option 17', description: 'Description 17' },
        { value: 18, text: 'Option 18', description: 'Description 18' },
        { value: 19, text: 'Option 19', description: 'Description 19' },
        { value: 20, text: 'Option 20', description: 'Description 20' },
        { value: 21, text: 'Option 21', description: 'Description 21' },
        { value: 22, text: 'Option 22', description: 'Description 22' },
        { value: 23, text: 'Option 23', description: 'Description 23' },
        { value: 24, text: 'Option 24', description: 'Description 24' },
        { value: 25, text: 'Option 25', description: 'Description 25' }
      ],
      multiSelectedWithTags: [],
      multiSelectedSmart: [],
      errorSelected: null,
      multiSelected: [],
      singleSelected: null,
      limitedSelected: [],
      selectedUser: null,
      lastSearch: '',
      userOptions: []
    }
  },

  computed: {
    filteredUsers() {
      if (!this.selectedRoles.length) {
        return this.users;
      }

      // "=" mode → include selected
      if (this.mode === '=') {
        return this.users.filter(u =>
            this.selectedRoles.includes(u.role)
        );
      }

      // "!=" mode → exclude selected
      if (this.mode === '!=') {
        return this.users.filter(u =>
            !this.selectedRoles.includes(u.role)
        );
      }

      return this.users;
    }
  },

  methods: {
    onSelect(item) {
      console.log('Selected:', item);
    },

    onDeselect(item) {
      console.log('Deleted:', item);
    },

    onChange(value) {
      console.log("After splitter transformation:", value);
    },

    canRemoveItem(item) {
      if (item.value === "admin") {
        return false;
      }

      return true;
    },

    getItemClass(item) {
      switch (item.value) {
        case "admin":
          return "bg-danger text-white fw-bold";

        case "manager":
          return "bg-primary text-white";

        case "user":
          return "bg-success text-white";

        default:
          return "";
      }
    },

    getTagColor(item) {
      switch (item.value) {
        case "admin":
          return "bg-danger text-white";

        case "manager":
          return "bg-primary text-white";

        case "user":
          return "bg-success text-white";

        default:
          return "bg-secondary text-white";
      }
    },

    fetchItems() {
      this.isLoading = true;
      this.list = [];

      // Simulate async API call
      setTimeout(() => {
        this.list = [
          { value: 1, text: 'Option One' },
          { value: 2, text: 'Option Two' },
          { value: 3, text: 'Option Three' }
        ];
        this.isLoading = false;
      }, 2000);
    },

    async handleSearch(searchText) {
      this.isLoading = true
      try {
        const { data } = await axios.get(
            `https://jsonplaceholder.typicode.com/users`,
            { params: { q: searchText } }
        )
        this.userOptions = data.map(user => ({
          id: user.id,
          name: user.name,
          description: user.email
        }))
      } catch (e) {
        console.error('API error:', e)
        this.userOptions = []
      } finally {
        this.isLoading = false
      }
    },

    customSearchHandler(searchText, items) {
      return items.filter(item =>
          item.name.toLowerCase().startsWith(searchText.toLowerCase())
      )
    }
  },
}

</script>


